import type React from 'react';
import {ColumnId, ColumnState} from "../player-columns/types";

export interface CommentMarker {
  id: number;
  pos: number;
  color: number;
  reply: number;
}

export interface CaptionCue {
  start: number;
  end: number;
  content: string;
}

export interface TrackInfo {
  label: string;
  srclang: string;
  src: string;
  kind: string;
}

export interface MediaSource {
  src: string;
  type: string;
  label: string;
  /** Proxy ID from data-proxy-id attribute. Present for proxy sources, undefined for originals. */
  proxyId?: string;
}

export interface KollabPlayerProps {
  /** Legacy: DOM id of the media element to adopt. Exactly one of `mediaElementId` or `mediaElementRef` must be provided. */
  mediaElementId?: string;
  /** Ref to the media element to adopt. Preferred over `mediaElementId`. Exactly one of the two must be provided. */
  mediaElementRef?: React.RefObject<HTMLMediaElement | null>;
  /** Ref to the element that should act as the fullscreen target/wrapper. When omitted, falls back to the media element's parent. */
  fullscreenTargetRef?: React.RefObject<HTMLElement | null>;
  mediaType?: 'video' | 'audio';
  fileId?: number;
  /** URL to a waveform image. When provided, the waveform toggle is shown for video files and the waveform is always visible for audio files. */
  waveformUrl?: string;
  /** Background color for the waveform area. Defaults to '#1a1a1a'. */
  waveformColor?: string;
  fps?: number;
  startFrame?: number;
  /** Start timecode string (e.g. "01:00:00:00" or "01:00:00;00").
   *  Converted to a frame offset internally. Takes precedence over startFrame. */
  startTimecode?: string;
  dropFrame?: boolean;
  enableHeatmap?: boolean;
  /** Track playback position for heatmap generation. Defaults to true. */
  trackHeatmap?: boolean;
  /** Shared-link ID for heatmap attribution (anonymous viewers). */
  linkId?: string;
  enableCaptions?: boolean;
  autoplay?: boolean;
  /** Start playback from this time (seconds). Seeks to this position once the media is ready. */
  startTime?: number;
  thumbnailSpriteUrl?: string;
  comments?: CommentMarker[];
  onTimeUpdate?: (time: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  /** Fires when playback reaches the end of the file. Does not fire when looping is on or when the user pauses. */
  onEnded?: () => void;
  onSeek?: (time: number) => void;
  /** Return false to block a pending seek (e.g. to confirm discarding unsaved annotations). */
  onBeforeSeek?: () => boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  canDownload?: boolean;
  downloadUrl?: string;
  fileTitle?: string;
  fileTypeName?: string;
  fileSize?: number;
  fileIconUrl?: string;
  /** DOM element ID to adopt into the fullscreen side panel. The element is moved back on fullscreen exit. */
  sidePanelElementId?: string;
  /** Width of the side panel in pixels. Defaults to 300. */
  sidePanelWidth?: number;
  /** Tooltip text shown on hover over the side panel toggle button. */
  sidePanelTooltip?: string;
  /** Called once after the player has mounted and the API is available. */
  onMounted?: () => void;
  /**
   * Whether this player is in the currently active column. When false in a
   * multi-column layout, clicking the video activates the column (via the
   * parent's onActivate handler) instead of toggling play/pause. Defaults
   * to true so single-column usage preserves the click-to-play behavior.
   */
  isActive?: boolean;
}

export interface KeyboardShortcut {
  key: string;
  description: string;
}

export interface KollabPlayerAPI {
  play: () => void;
  pause: () => void;
  isPlaying: () => boolean;
  getPlaybackRate: () => number;
  setPlaybackRate: (rate: number) => void;
  /**
   * Sync-handler overlay for small drift-correction rate nudges from the
   * multi-column playback sync code. Sets ONLY the media element's
   * playbackRate; does not touch `userPlaybackRateRef`, dispatch the
   * reducer, or flash the OSD. The sync handler MUST use this instead of
   * `setPlaybackRate` so its corrections don't corrupt the user's chosen
   * rate (which `handlePlayClick` restores on each resume-from-pause).
   */
  setSyncPlaybackRate: (rate: number) => void;
  /**
   * Clear any sync-handler rate overlay by restoring the media element's
   * playbackRate from the user's chosen rate. No-op if no overlay is
   * active. Called by the sync handler on re-subscribe (active column
   * change) to drop stale corrections.
   */
  clearSyncPlaybackRate: () => void;
  seekToTime: (time: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getMediaElement: () => HTMLMediaElement | null;
  /** Returns the player's outermost container element (.kollab-player). */
  getContainerElement: () => HTMLElement | null;
  updateComments: (comments: CommentMarker[]) => void;
  showDownloadOverlay: (message?: string) => void;
  seekToNextMarker: () => void;
  seekToPreviousMarker: () => void;
  getKeyboardShortcuts: () => KeyboardShortcut[];
  captureFrame: (options?: import('../annotations/utils/frameExport').FrameExportOptions) => Promise<Blob>;
  focus: (options?: FocusOptions) => void;
  isFullscreen: () => boolean;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  setSidePanelOpen: (open: boolean) => void;
  /** Move a DOM element into the side panel. Pass null to remove. The element is restored to its original position on fullscreen exit. */
  setSidePanelElement: (elementId: string | null) => void;
  /** Set the side panel width in pixels. */
  setSidePanelWidth: (width: number) => void;
  /** Select a caption track by language code (e.g. 'en', 'es'). Pass null or empty string to disable captions. */
  selectCaptionLanguage: (lang: string | null) => void;
  /** Show or hide the audio waveform view */
  setWaveformVisible: (visible: boolean) => void;
  /** Whether the "Pause while typing" setting is enabled. */
  getPauseWhileTyping: () => boolean;
  /** Whether the autoplay setting is enabled. */
  getAutoplay: () => boolean;
  /** Whether comment markers are visible. */
  getShowMarkers: () => boolean;
  /** Whether captions auto-enable when tracks are available. */
  getAutoCaptions: () => boolean;
  setMuted: (muted: boolean) => void;
  subscribeToPlaybackEvents: (cb: PlayerEventCallback) => PlaybackEventSubscribeHandle;
  getFrameRate: () => number;
  /** Ramp playback rate toward negative direction (one step per call).
   *  Uses native reverse playback when the browser supports it, otherwise
   *  falls back to a setInterval-driven fake rewind that decrements
   *  currentTime. */
  rewind: () => void;
  /** Ramp playback rate toward positive direction (one step per call).
   *  Starts playback from paused at 1x, or increments the rate in the
   *  existing ramp schedule. No-op when the video has ended. */
  fastForward: () => void;
  /** Begin stepping backward one frame every 200ms (~5fps shuttle).
   *  Idempotent — calling again while already shuttling is a no-op. */
  startSlowStepBack: () => void;
  /** Begin stepping forward one frame every 200ms. Idempotent. */
  startSlowStepForward: () => void;
  /** Stop any in-progress slow step (forward or back). */
  stopSlowStep: () => void;
  /** Stop any in-progress fake-rewind interval and zero the rewind rate.
   *  Does not ramp playback — use pause() or play() for that. */
  clearRewind: () => void;
}

export interface KollabPlayerHandle {
  readonly type: 'video' | 'audio';
  unmount: () => void;
  /** Re-render the existing React root with new props. Used by the player
   *  dispatcher for in-place file switches that preserve fullscreen state
   *  and the side panel adoption. The caller must update the underlying
   *  <video>/<audio> element's <source> children BEFORE calling this so
   *  usePlaybackErrorHandler's effect picks up the new sources when it re-runs. */
  rerender: (props: KollabPlayerProps) => void;
  readonly api: KollabPlayerAPI | null;
}

declare global {
  interface Window {
    mountKollabPlayer: (containerId: string, props: KollabPlayerProps) => KollabPlayerHandle;
  }
}

export const enum PlayerEventType {
  PLAY = 'play',
  PAUSE = 'pause',
  SEEK = 'seek',
  TIME_UPDATE = 'time_update',
  SPEED_CHANGE = 'speed_change',
  CLEAR_REWIND = 'clear_rewind',
  REWIND = 'rewind',
  FAST_FORWARD = 'fast_forward',
  START_SLOW_STEP_FORWARD = 'start_slow_step_forward',
  START_SLOW_STEP_BACK = 'start_slow_step_back',
  STOP_SLOW_STEP = 'stop_slow_step',
}

export interface PlaybackEvent {
  type: PlayerEventType;
  currentTime: number;
  playbackRate: number;
  isPaused: boolean;
}

export interface PlaybackEventSubscribeHandle {
  unsubscribe: () => void;
}

export type PlayerEventCallback = (event: PlaybackEvent) => void;
